import {
  AssetResultDTO,
  ParentRelationshipType,
  TaskTypeDetailedDTO2,
  WorkTaskDTO2,
  WorkPermitRequirement,
  WorkTaskRelativeDTO,
  WorkTaskStatus
} from '../api/api';
import { TaskCreationTask } from '../models/TaskCreationTask';
import { TaskComponent } from '../models/TaskTypeComponent';
import { extractAttributeValuesFromComponents } from './componentHandling';
import { getWorkPermitTechnicianDepartmentId } from './constants';
import { getWorkPermitTimes } from './workPermitTimes';

/**
 * A work permit (Arbejdstilladelse (KOS)) belongs to the main task. It is usually created by a
 * technician while doing a preparation task for the main task, so in that case the main task is the
 * preparation task's parent.
 */
export const getWorkPermitMainTaskId = (task: WorkTaskDTO2): number | undefined => {
  const isPreparationTask =
    task.parentRelationshipType === ParentRelationshipType.PreparationForParent || !!task.taskType?.isPreparationTask;

  if (isPreparationTask && task.parent?.workTaskId) return task.parent.workTaskId;

  return task.id ? parseInt(task.id) : undefined;
};

/**
 * A work permit sent back to the technician is in the technician department until the technician sends it to
 * Kontrolrummet.
 */
export const isWorkPermitWaitingForTechnician = (workPermit: WorkTaskDTO2) =>
  workPermit.status === WorkTaskStatus.Created &&
  workPermit.assignedToDepartment?.departmentId === getWorkPermitTechnicianDepartmentId();

/**
 * The immediate hierarchy of a task: its children, its parent and, through the parent, its siblings.
 */
export const getRelatives = (task: WorkTaskDTO2, parentTask: WorkTaskDTO2 | undefined): WorkTaskRelativeDTO[] => [
  ...(task.children ?? []),
  ...(task.parent ? [task.parent] : []),
  ...(parentTask?.children ?? [])
];

const isActiveWorkPermit = (relative: WorkTaskRelativeDTO, workPermitTaskTypeId: number) =>
  relative.taskTypeId === workPermitTaskTypeId && relative.status !== WorkTaskStatus.Canceled;

/**
 * A work permit is created for the main task, so a task is covered by one among its own relatives or,
 * when it is a preparation or follow-up task, among its parent's relatives. A canceled work permit
 * does not count.
 */
export const hasWorkPermitTask = (
  task: WorkTaskDTO2,
  parentTask: WorkTaskDTO2 | undefined,
  workPermitTaskTypeId: number
): boolean => getRelatives(task, parentTask).some((relative) => isActiveWorkPermit(relative, workPermitTaskTypeId));

/**
 * The ids of the work permits in the immediate hierarchy of a task, including the task itself when it is
 * one. Canceled work permits are left out.
 */
export const getWorkPermitTaskIds = (
  task: WorkTaskDTO2,
  parentTask: WorkTaskDTO2 | undefined,
  workPermitTaskTypeId: number
): number[] => {
  const self: WorkTaskRelativeDTO = {
    workTaskId: task.id ? parseInt(task.id) : undefined,
    taskTypeId: task.taskType?.id,
    status: task.status
  };

  const ids = [self, ...getRelatives(task, parentTask)]
    .filter((relative) => relative.workTaskId && isActiveWorkPermit(relative, workPermitTaskTypeId))
    .map((relative) => relative.workTaskId as number);

  return Array.from(new Set(ids));
};

/**
 * Work permits matter for a task when the task, or a task in its immediate hierarchy, is a work permit
 * or has a task type that requires one. The task types of the relatives are not part of the task, so
 * the caller passes the ids of the task types that require a work permit.
 */
export const isWorkPermitRelevant = (
  task: WorkTaskDTO2,
  parentTask: WorkTaskDTO2 | undefined,
  workPermitTaskTypeId: number,
  taskTypeIdsRequiringWorkPermit: number[]
): boolean => {
  const isOrRequiresWorkPermit = (taskTypeId?: number) =>
    taskTypeId !== undefined && (taskTypeId === workPermitTaskTypeId || taskTypeIdsRequiringWorkPermit.includes(taskTypeId));

  return (
    task.taskType?.workPermitRequirement === WorkPermitRequirement.Required ||
    isOrRequiresWorkPermit(task.taskType?.id) ||
    getRelatives(task, parentTask).some((relative) => isOrRequiresWorkPermit(relative.taskTypeId))
  );
};

/**
 * The fields the create work task flow starts out with for a work permit, so the technician lands
 * directly on the details step with the task type, asset, dates and technician of the main task.
 */
export const getWorkPermitTaskSeed = (
  mainTask: WorkTaskDTO2,
  workPermitTaskType: TaskTypeDetailedDTO2,
  asset: AssetResultDTO
): Partial<TaskCreationTask> => {
  const componentsFromParent = extractAttributeValuesFromComponents(mainTask.components ?? []);
  const times = getWorkPermitTimes(mainTask);

  return {
    parentId: mainTask.id ? parseInt(mainTask.id) : undefined,
    creationType: 'relational',
    relation: { parentRelationshipType: ParentRelationshipType.PreparationForParent },
    parentRelationshipType: ParentRelationshipType.PreparationForParent,
    minTimeBeforeMainTaskDays: workPermitTaskType.isPreparationTask
      ? workPermitTaskType.minTimeBeforeMainTaskDays
      : undefined,
    maxTimeBeforeMainTaskDays: workPermitTaskType.isPreparationTask
      ? workPermitTaskType.maxTimeBeforeMainTaskDays
      : undefined,
    parentEarliestStartDate: mainTask.earliestStartDate,
    parentDeadline: mainTask.deadline,
    earliestStartDate: times?.start,
    deadline: times?.end,

    taskTypeId: workPermitTaskType.id,
    taskType: workPermitTaskType,
    // The work permit form hides the norm time fields, so they get values that pass validation
    normTimeMin: workPermitTaskType.normTimeMin || 60,
    normTimeValidated: true,
    projectNumber: workPermitTaskType.projectNumber_OneERP,
    assignedToDepartmentId:
      workPermitTaskType.defaultDepartment?.departmentId ?? mainTask.assignedToDepartment?.departmentId,
    ignoreDrivingTimeWhenPlanned: workPermitTaskType.ignoreDrivingTimeWhenPlanned,
    componentsFromParent,
    components: workPermitTaskType.taskComponents?.map(
      (component) =>
        ({
          ...component,
          attributes: component.attributes?.map((attribute) => {
            const attributeFromParent = componentsFromParent.find((cfp) => cfp.attributeId === attribute.attributeId);
            return attributeFromParent?.value ? { ...attribute, value: attributeFromParent.value } : { ...attribute };
          })
        } as TaskComponent)
    ),

    assignedToEmail: mainTask.assignedToEmail,
    assignedToName: mainTask.assignedToName,

    description: mainTask.description,
    taskLocation: mainTask.taskLocation,
    gisRegion: mainTask.gisRegion,
    stretches: mainTask.stretches,
    assetId: mainTask.assetId,
    assetLocation: asset.location,
    assetType: asset.assetType,
    assetPrimaryVoltage: asset.primaryVoltage,
    assetGisMigrationId: asset.assetGisMigrationId,
    assetDynamicsFunctionalLocationId: asset.assetDynamicsFunctionalLocationId
  };
};
